import { useFieldArray, useForm } from "react-hook-form";
import KaAlert, { DisplayMessage } from "../../common/lib/KaAlert";
import { sumWeights } from "../../common/utilities";
import Container, { Progress } from "../../common/Container";
import Footer from "../../common/Footer";
import FooterButton from "../../common/FooterButton";
import { Compartment } from "../../requests/useCreateStagedOrder";
import { FooterProps } from "./StageOrderTypes";
import SwitchToMobileQRCode from "../../common/SwitchToMobileQRCode";
import useIsAnonymous from "../../common/lib/hooks/useIsAnonymous";
import { useLingui } from "@lingui/react";
import { msg, Trans } from "@lingui/macro";
import StartOverButton from "../../common/StartOverButton";
import { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface ConfigureLoadProps extends FooterProps {
  availableToRequest: number,
  availableToRequestVolume?: number,
  estimatedDensity?: number,
  volumeToggleAllowed: boolean,
  orderNumber: string,
  message: DisplayMessage | null,
  clearMessage: () => void,
  initialCompartments?: Compartment[],
  compartmentsConfigured: (compartments: Compartment[]) => void,
  progress?: Progress,
  allowExceedOrderAmount: boolean;
  startOver: () => void;
}

interface ConfigureLoadForm {
  compartments: { name: string, weight: number }[]
}

const ConfigureLoad = (props: ConfigureLoadProps) => {
  const { _ } = useLingui();
  const isAnonymous = useIsAnonymous();
  const maxCompartments = 8;
  const volumeToggleEnabled = props.volumeToggleAllowed && 
    props.availableToRequestVolume != undefined && 
    props.estimatedDensity != undefined;
  const [volumeActive, setVolumeActive] = useState<boolean>(false);

  const toFormCompartments = (compartments: Compartment[]) =>
    compartments.map(({ weight }, i) => ({ name: _(msg`Compartment ${i + 1}`), weight }));

  const { register, handleSubmit, watch, control, formState: { errors } } = useForm<ConfigureLoadForm>({
    defaultValues: {
      compartments: props.initialCompartments
        ? toFormCompartments(props.initialCompartments)
        : [{ name: _(msg`Compartment 1`), weight: undefined }]
    }
  });

  const compartments = watch('compartments');

  const fa = useFieldArray({ control, name: 'compartments' });

  const addCompartment = () => {
    fa.append({ name: _(msg`Compartment ${fa.fields.length + 1}`) }, { shouldFocus: false });
  }

  const removeCompartment = () => {
    fa.remove(fa.fields.length - 1);
  }

  const allocatedWeight = !volumeActive ? sumWeights(compartments) : sumWeights(compartments) * props.estimatedDensity!;
  const allocatedVolume = !volumeActive ? null : sumWeights(compartments);

  const submit = (form: ConfigureLoadForm) => {
    const compartments = form.compartments.map(c => ({ weight: volumeActive ? c.weight * props.estimatedDensity! : c.weight }));
    props.compartmentsConfigured(compartments);
  }

  const doCompartmentsHaveInvalidWeights = (compartments: Compartment[], allowExceedOrderAmount: boolean) => {
    if (!allowExceedOrderAmount && allocatedWeight > props.availableToRequest) {
      return true;
    }
    return compartments.some(c => c.weight === undefined || isNaN(c.weight));
  }

  const NumberRowWithLabel = (props: { label: string, number: number, invalid: boolean, volume: boolean, marginBelow: number}) => <>
    <div className="row">
      <label className={`${props.volume ? 'fs-6' : 'h5'} text-end px-0`}>
        {props.label}
      </label>
    </div>
    <div className={`row mb-${props.marginBelow} text-end`}>
      <label className={`${props.volume ? 'fs-6' : 'h5'} ${props.invalid ? "text-danger" : ""}`}>
        {!props.volume ? _(msg`${props.number} lb`) : _(msg`${props.number} gal`)}
      </label>
    </div>
  </>

  return <Container
    title={_(msg`Configure Load`)}
    subtitle={_(msg`Add Compartments`)}
    progress={props.progress}>

    {isAnonymous && <>
      <StartOverButton startOver={props.startOver}/>
      <SwitchToMobileQRCode />
    </>}
    <div className="my-auto">
      <div className="mx-auto mb-lg-6">
        <div className="col-auto">
          <label className="mt-1 d-flex justify-content-center text-black h2">
            <Trans>Number of compartments:</Trans>
          </label>
          <div className="mb-2 d-flex justify-content-center h-100">
            <button
              title={'removeCompartment'}
              className="btn btn-ghost-dark btn-icon rounded-circle me-3"
              onClick={removeCompartment}
              disabled={compartments.length <= 1}>
              <i className="bi-dash" style={{ fontSize: 45 }}></i>
            </button>
            <label className="text-black h2 mt-2">
              {fa.fields.length}
            </label>
            <button
              title={'addCompartment'}
              className="btn btn-ghost-dark btn-icon rounded-circle ms-3 "
              onClick={addCompartment}
              disabled={compartments.length >= maxCompartments}>
              <i className="bi-plus" style={{ fontSize: 45 }}></i>
            </button>
          </div>
        </div>
      </div>
      <div className="mx-auto w-85">
        <KaAlert displayMessage={props.message} onClose={props.clearMessage} />
        <div className="row">
          <div className="col-lg-2" />
          <div className="col-7 col-lg-4 overflow-auto pe-2">
            {fa.fields.map((field, index) => {
              const id = `compartment${index + 1}`;
              const error = errors?.compartments?.at(index);
              return <div key={field.id}>
                <label className="h5 mb-2" htmlFor={id}>
                  {field.name}
                </label>
                <div className="input-group mb-4">
                  <input
                    id={id}
                    type="number"
                    autoComplete={!isAnonymous ? "on" : "off"}
                    className={`form-control ${!!error && 'is-invalid'}`}
                    {...register(`compartments.${index}.weight` as const, {
                      onChange: () => watch(),
                      valueAsNumber: true,
                      required: _(msg`Compartment weight required`),
                      validate: {
                        positive: v => v > 0 || _(msg`Must be greater than 0`),
                      },
                    })}
                  />
                  <div className="input-group-append input-group-text fs-5 px-2 text-center">
                    <button
                      title={`toggleMass-${id}`}
                      className={`btn btn-sm p-0 mx-1 ${volumeActive ? '' : 'text-primary fw-bold'}`} 
                      onClick={() => setVolumeActive(false)}>
                        <Trans>lb</Trans>
                    </button>
                  </div>
                  {props.volumeToggleAllowed && <OverlayTrigger
                    placement={'top'}
                    delay={{ show: 250, hide: 400 }}
                    overlay={!volumeToggleEnabled ?
                      <Tooltip>
                        <Trans>Volume conversion is not available for this order</Trans>
                      </Tooltip> :
                      <></>
                    }>
                    <div className="input-group-append input-group-text fs-5 px-2 text-center">
                      <button
                        title={`toggleVolume-${id}`}
                        className={`btn btn-sm p-0 m-0 ${volumeActive ? 'text-primary fw-bold' : ''}`} 
                        onClick={() => setVolumeActive(true)}
                        disabled={!volumeToggleEnabled}>
                          <Trans>gal</Trans>
                      </button>
                    </div>
                  </OverlayTrigger>}
                  <span className="invalid-feedback">{error?.weight?.message}</span>
                </div>
              </div>
            })}
          </div>
          <div className="col-5 col-lg-4">
            <NumberRowWithLabel label={_(msg`Weight Remaining`)} number={parseFloat((props.availableToRequest - allocatedWeight).toFixed(2))} invalid={allocatedWeight > props.availableToRequest} volume={false} marginBelow={volumeActive ? 0 : 5}/>
            {volumeActive && <NumberRowWithLabel label={_(msg`Volume Remaining`)} number={parseFloat((props.availableToRequestVolume! - allocatedVolume!).toFixed(2))} invalid={allocatedWeight > props.availableToRequest} volume={true} marginBelow={3}/>}
            <NumberRowWithLabel label={_(msg`Weight Allocated`)} number={parseFloat(allocatedWeight.toFixed(2))} invalid={allocatedWeight > props.availableToRequest} volume={false} marginBelow={volumeActive ? 0 : 5} />
            {volumeActive && <NumberRowWithLabel label={_(msg`Volume Allocated`)} number={parseFloat(allocatedVolume!.toFixed(2))} invalid={allocatedWeight > props.availableToRequest} volume={true} marginBelow={3} />}
          </div>
        </div>
      </div>
    </div>
    <Footer
      backButton={<FooterButton {...props.backButton} />}
      nextButton={<FooterButton
        onClick={handleSubmit(submit)}
        disabled={doCompartmentsHaveInvalidWeights(compartments, props.allowExceedOrderAmount)}
        {...props.nextButton} />}
    />
  </Container>
}

export default ConfigureLoad;
